import React, { useContext } from "react";
import { dailyMealsPath, DESSERT_DAYS_PATH, mealsPath, menuPath, ORDERS_PATH, SIDE_DAYS_PATH, snacksPath, studentsPath } from "../../functions/src/admin/paths";
import { getWeekDays, getWeekdayName, toDateFromServer } from "../../functions/src/api/dates";
import { auth, currentCatering, currentDailyMeals, currentMenu, DataSnapshot } from "../api/firebase";
import { useDatabase, useList } from "../api/firebaseHooks";
import { getLangValue, langAsset, langContext, localeFromLang } from "../api/Lang";
import { useOrderStrings } from "./order-strings";
import { getMealData } from "../api/mealUtils";
import { Meal, MealLine, OrderPerson, Snack, SnackLine } from "../types";

export default function WeeklyOrders({ date }: { date: Date }) {

    const [lang] = useContext(langContext);
    const orderStrings = useOrderStrings();
    const { uid } = auth.currentUser;
    const orderWeekDays = getWeekDays(date);

    const students = useDatabase(studentsPath(uid));
    const studentsList: DataSnapshot<OrderPerson>[] = useList(students);

    return <div >
        <div className="header">
            <img className='logo' src={langAsset('gefsinusLogoDark', lang)} alt="Gefsinus" width='150px' />
            <img className='logo' src='/img-assets/logo-light.svg' alt="Lunch Box" width='150px' />
        </div>

        <div className="print-orders-header">{orderStrings.printOrdersWord}{` ${toDateFromServer(orderWeekDays[0]).toLocaleDateString(localeFromLang(lang))} - ${toDateFromServer(orderWeekDays[4]).toLocaleDateString(localeFromLang(lang))}`}</div>
        {studentsList?.map((s: DataSnapshot<OrderPerson>) => {
            const personName = students?.child(s.key)?.child('firstName')?.val() + " " + students?.child(s.key)?.child('lastName')?.val();
            return <div key={s.key} >
                <div className="print-orders-content">{personName}</div>
                <table className="table-order" style={{ marginBottom: '50px' }}>
                    <thead>
                        <tr>
                            <th>{orderStrings.printColDay}</th>
                            <th className='product-max-width'>{orderStrings.printColMeal}</th>
                            <th >{orderStrings.printColQty}</th>
                            <th className='product-max-width'>{orderStrings.printColSide}</th>
                            <th className='product-max-width'>{orderStrings.printColDessert}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orderWeekDays.map(orderDate => <OrderPerUser key={orderDate} uid={uid} orderDate={orderDate} studentId={s.key} />)}
                    </tbody>
                </table>
                {/* <div style={{ breakAfter: 'always' }}> </div> */}
            </div>
        })}
    </div>
}

function OrderPerUser({ uid, orderDate, studentId }: OrderPerUserProps) {

    const [lang] = useContext(langContext);
    const mealsOrder = useDatabase(`${ORDERS_PATH}/${orderDate}/${uid}/meals/`, ref => ref.orderByChild('recipient').equalTo(studentId));
    const snacksOrder = useDatabase(`${ORDERS_PATH}/${orderDate}/${uid}/snacks/`, ref => ref.orderByChild('recipient').equalTo(studentId));
    const mealsOrderList: DataSnapshot<MealLine>[] = useList(mealsOrder);
    const snacksOrderList: DataSnapshot<SnackLine>[] = useList(snacksOrder);

    return <>
        {mealsOrderList?.map((mealOrder) => <MealPerUser key={mealOrder.key} mealOrderVal={mealOrder.val()} orderDate={orderDate} />)}
        {snacksOrderList?.map((snackOrder) => <SnackPerUser key={snackOrder.key} snackOrderVal={snackOrder.val()} orderDate={orderDate} />)}
        {(!mealsOrderList?.length && !snacksOrderList?.length) &&
            <tr key={mealsOrder?.key}>
                <td >{getWeekdayName(toDateFromServer(orderDate).getDay(), lang)}</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
            </tr>}
    </>
}

type OrderPerUserProps = {
    uid: string
    orderDate: string
    studentId: string
}

function MealPerUser({ mealOrderVal, orderDate }: MealPerUserProps) {

    const [lang] = useContext(langContext);
    const mealKey = mealOrderVal.key;

    // Get traditional meals and daily meals (same approach as Order.tsx)
    const sFoods = useDatabase(mealsPath(currentCatering));
    const sDailyMeals = useDatabase(dailyMealsPath(currentDailyMeals), ref => ref.orderByChild('order').startAt(0));
    const dailyMealsList: DataSnapshot<Meal>[] = useList(sDailyMeals) || [];

    // Get menu items for the date to use correct pricing for menu items
    const menusSnapshot = useDatabase(`${menuPath(currentMenu)}/${orderDate}`);
    const menuItems = useList(menusSnapshot);
    const menuData: { [key: string]: Meal } = {};

    if (menuItems) {
        menuItems.forEach((menuItemSnapshot) => {
            const menuItem = menuItemSnapshot.val();
            if (menuItem && menuItem.key) {
                // Use the meal key as the key for menuData
                menuData[menuItem.key] = menuItem;
            }
        });
    }

    // Use helper function to get meal data (handles both traditional and daily meals)
    const meal = getMealData(mealKey, sFoods, dailyMealsList, menuData);
    const mealName: string = getLangValue(meal?.name, lang) ?? mealKey;

    const sideDish: Meal = useDatabase(`${menuPath(currentMenu)}/${orderDate}/sideMenu`)?.val();
    const mealSide = meal?.sideDish?.[lang];
    const sideDay: string = useDatabase(`${SIDE_DAYS_PATH}/${orderDate}/${lang}`)?.val() ?? '';
    const sideMenu: string = !sideDish ? '' : getLangValue(sideDish.name, lang);
    const sideName: string = !mealSide ? !sideMenu ? sideDay : sideMenu : mealSide;

    const dessert: Meal = useDatabase(`${menuPath(currentMenu)}/${orderDate}/dessertMenu`)?.val();
    const dessertDay: string = useDatabase(`${DESSERT_DAYS_PATH}/${orderDate}/${lang}`)?.val() ?? '';
    const dessertMenu: string = !dessert ? '' : getLangValue(dessert.name, lang);
    const dessertName: string = !dessertMenu ? dessertDay : dessertMenu;

    return <tr key={mealKey}>
        <td >{getWeekdayName(toDateFromServer(orderDate).getDay(), lang)}</td>
        <td>{mealName}</td>
        <td style={{ textAlign: 'center' }}>{mealOrderVal.quantity}</td>
        <td>{mealOrderVal.quantitySide && sideName}</td>
        <td>{mealOrderVal.quantityDessert && dessertName}</td>
    </tr>
}

type MealPerUserProps = {
    mealOrderVal: MealLine
    orderDate: string
}

function SnackPerUser({ snackOrderVal, orderDate }: SnackPerUserProps) {

    const [lang] = useContext(langContext);
    const snackKey = snackOrderVal.snackKey;

    const snack: Snack = useDatabase(snackKey ? snacksPath(`${currentCatering}/${snackKey}`) : null)?.val();
    const snackName: string = getLangValue(snack?.name, lang) ?? snackKey;

    return <tr key={snackKey}>
        <td >{getWeekdayName(toDateFromServer(orderDate).getDay(), lang)}</td>
        <td>{snackName}</td>
        <td style={{ textAlign: 'center' }}>{snackOrderVal.quantity}</td>
        <td></td>
        <td></td>
    </tr>
}

type SnackPerUserProps = {
    snackOrderVal: SnackLine
    orderDate: string
}